// Time windows of the journal's volume chart and the buckets that fill them.
// Pure: the API counts per bucket in SQL, the page fills the empty buckets
// with `bucketize`, and both agree on where a bucket starts.
import type { ActivityOutcome } from "@/lib/activity/outcome";

export type TimeRange = "1h" | "24h" | "7d" | "30d";

export const TIME_RANGE_ORDER = ["1h", "24h", "7d", "30d"] as const satisfies readonly TimeRange[];

export const TIME_RANGES = {
  "1h": { label: "Dernière heure", minutes: 60, bucketMinutes: 5, bucketLabel: "Par 5 minutes" },
  "24h": {
    label: "Dernières 24 heures",
    minutes: 1440,
    bucketMinutes: 60,
    bucketLabel: "Par heure",
  },
  "7d": {
    label: "7 derniers jours",
    minutes: 10_080,
    bucketMinutes: 360,
    bucketLabel: "Par 6 heures",
  },
  "30d": {
    label: "30 derniers jours",
    minutes: 43_200,
    bucketMinutes: 1440,
    bucketLabel: "Par jour",
  },
} as const satisfies Record<
  TimeRange,
  { label: string; minutes: number; bucketMinutes: number; bucketLabel: string }
>;

const MINUTE_MS = 60_000;

/** Offsets beyond the real-world extremes are a malformed request. */
const MAX_OFFSET_MS = 14 * 60 * MINUTE_MS;

export const rangeMs = (range: TimeRange) => TIME_RANGES[range].minutes * MINUTE_MS;
export const bucketMs = (range: TimeRange) => TIME_RANGES[range].bucketMinutes * MINUTE_MS;

export function clampOffset(offsetMs: number): number {
  return Number.isFinite(offsetMs)
    ? Math.min(MAX_OFFSET_MS, Math.max(-MAX_OFFSET_MS, Math.round(offsetMs)))
    : 0;
}

/**
 * Start of the bucket holding `at`. Buckets align to the viewer's local
 * clock (`offsetMs` is local minus UTC), so daily bars begin at local midnight.
 */
export function bucketStartOf(at: number, range: TimeRange, offsetMs: number): number {
  const size = bucketMs(range);

  return Math.floor((at + offsetMs) / size) * size - offsetMs;
}

export type VolumeCounts = Record<ActivityOutcome, number>;

/** `start` and `end` clamp to the window, so an edge bucket reads as the part it covers. */
export type Bucket = { key: string; start: number; end: number } & VolumeCounts;

/** A contiguous run of buckets, first and last key inclusive. */
export type BucketSpan = { from: string; to: string };

export interface BucketCount extends VolumeCounts {
  /** UTC ms of the bucket start, as `bucketStartOf` returns it. */
  start: number;
}

/** Every bucket of the window, empty ones included, with the server's counts poured in. */
export function bucketize(
  counts: readonly BucketCount[],
  range: TimeRange,
  windowEnd: number,
  offsetMs: number,
): Bucket[] {
  const size = bucketMs(range);
  const windowStart = windowEnd - rangeMs(range);
  const first = bucketStartOf(windowStart, range, offsetMs);
  const last = bucketStartOf(windowEnd - 1, range, offsetMs);
  const byStart = new Map(counts.map((count) => [count.start, count]));
  const buckets: Bucket[] = [];

  for (let start = first; start <= last; start += size) {
    const found = byStart.get(start);
    buckets.push({
      key: new Date(start).toISOString(),
      start: Math.max(start, windowStart),
      end: Math.min(start + size, windowEnd),
      success: found?.success ?? 0,
      denied: found?.denied ?? 0,
      failed: found?.failed ?? 0,
    });
  }

  return buckets;
}
